/**
 * DesktopTopbar — the in-content top bar, mounted on EVERY breakpoint.
 *
 * nav-shell R1 (Increment 1): the navigation destinations (Cooks / Groups /
 * Feed) and the account avatar were folded into the single NavTray. This bar
 * now carries ONLY: an optional menu (hamburger) control on the left summoning
 * the NavTray overlay (mobile / tablet only), the `proof.` wordmark, an optional
 * section title, the saved-state chip, and Search.
 *
 * Layout (design.md §2):
 *   - [☰ menu (mobile only)] · `proof.` wordmark · title · … · saved · Search
 *   - On desktop (≥1024px) the NavTray is docked open as a left column, so the
 *     menu control is CSS-hidden — there is nothing to summon.
 *
 * The `proof.` wordmark carries the ONE sanctioned accent on this bar (its
 * trailing period). When `onLibrary` is wired the wordmark is the labelled
 * Home affordance (aria-label="Home", → the /app dashboard); otherwise it is a
 * static brand mark.
 *
 * Design refs:
 *   design.md §2 Core principle 2 — hairlines over shadows.
 *   design.md §2 Core principle 3 — one accent (budgeted to the wordmark period).
 *   design.md §6.0a — labelled primary-nav entries on every breakpoint (the menu
 *     control carries an aria-label; the tray it opens holds the labelled rows).
 *   design.md §15.3 — 44×44 tap targets.
 *   branding.md §5.2.1 — no banned words.
 *   CLAUDE.md Rule 7 — mobile + tablet + desktop from the same bar.
 */

import "./DesktopTopbar.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DesktopTopbarProps {
  /** Title shown after the wordmark (e.g. "Library"). Hidden on mobile. */
  title?: string;
  /** Save state label (e.g. "saved · 9:41"). Renders only when present. */
  savedLabel?: string;
  /**
   * When set, the menu (hamburger) control renders at the left of the bar and
   * summons the NavTray overlay. Hidden on desktop (≥1024px) where the tray is
   * docked open. Omit on surfaces that have no tray to summon.
   */
  onMenu?: () => void;
  /** aria-expanded for the menu control — true when the NavTray overlay is open. */
  menuExpanded?: boolean;
  /**
   * When set, the `proof.` wordmark becomes the labelled Home affordance
   * (aria-label="Home") and navigates to the /app dashboard. Omit when the user
   * is already on the home landing (the wordmark renders as a static brand
   * mark). Named `onLibrary` for historical reasons — pre dashboard/library
   * split (2026-07-11) `/app` WAS the library landing.
   */
  onLibrary?: () => void;
}

// ── Icon SVGs ─────────────────────────────────────────────────────────────────

function IconMenu() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3 5.5h14M3 10h14M3 14.5h14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      {/* Magnifying glass — circle lens + handle */}
      <circle cx="7" cy="7" r="4.25" stroke="currentColor" strokeWidth="1.5" />
      <line
        x1="10.3"
        y1="10.3"
        x2="13.5"
        y2="13.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * `proof.` wordmark — the accent period is the one sanctioned brand accent
 * exception (design.md §2 / plan §8). When `onLibrary` is wired the wordmark is
 * the labelled Home affordance (a real button, aria-label="Home", navigating to
 * the /app dashboard); otherwise it renders as a static brand mark (the user is
 * already on the home landing).
 */
function Wordmark({ onLibrary }: { onLibrary?: () => void }) {
  const inner = (
    <>
      proof
      <span className="proof-desktop-topbar__wordmark-dot" aria-hidden="true">
        .
      </span>
    </>
  );
  if (onLibrary !== undefined) {
    return (
      <button
        type="button"
        className="proof-desktop-topbar__wordmark"
        onClick={onLibrary}
        data-testid="desktop-topbar-home"
        aria-label="Home"
      >
        {inner}
      </button>
    );
  }
  return (
    <span
      className="proof-desktop-topbar__wordmark proof-desktop-topbar__wordmark--static"
      data-testid="desktop-topbar-home"
    >
      {inner}
    </span>
  );
}

export function DesktopTopbar({
  title,
  savedLabel,
  onMenu,
  menuExpanded = false,
  onLibrary,
}: DesktopTopbarProps) {
  return (
    <header
      className="proof-desktop-topbar"
      role="banner"
      aria-label="Top bar"
      data-testid="desktop-topbar"
    >
      <div className="proof-desktop-topbar__brand">
        {/*
         * Menu (hamburger) — summons the NavTray overlay on mobile / tablet.
         * CSS-hidden ≥1024px (the docked tray / mini-rail owns the summon there).
         * Carries the stable `data-testid="user-menu-trigger"` so the
         * discoverability e2e specs open the tray from this control (the testid
         * moved from the retired account avatar to the tray's summon control,
         * nav-shell R1). A clean, borderless, icon-only square — the operator
         * rejected the bulkier `[≡ Menu]` treatment (a visible "Menu" label that
         * pushed the brand off the leading edge), so there is no standing text
         * label; the aria-label "Menu" keeps it a labelled control (design.md
         * §6.0a / §2.8 — never a bare unlabelled icon) and the tray it opens
         * carries the labelled rows.
         */}
        {onMenu !== undefined && (
          <button
            type="button"
            className="proof-desktop-topbar__menu"
            aria-label="Menu"
            aria-haspopup="menu"
            aria-expanded={menuExpanded}
            onClick={onMenu}
            data-testid="user-menu-trigger"
          >
            <IconMenu />
          </button>
        )}

        <Wordmark {...(onLibrary !== undefined ? { onLibrary } : {})} />
        {title !== undefined && (
          <span className="proof-desktop-topbar__title" data-testid="desktop-topbar-title">
            {title}
          </span>
        )}
      </div>

      <div className="proof-desktop-topbar__actions">
        {savedLabel !== undefined && (
          <span
            className="proof-desktop-topbar__saved"
            data-testid="desktop-topbar-saved"
            aria-live="polite"
          >
            {savedLabel}
          </span>
        )}

        {/*
         * Search — hollow, labelled control (search revival, 2026-06-14). Opens
         * the palette via the global "proof:open-palette" CustomEvent that both
         * <Palette> mounts already listen for. Hollow (1px --line border) — accent
         * stays reserved for the wordmark period (design.md §2 Core principle 3).
         */}
        <button
          type="button"
          className="proof-desktop-topbar__search"
          aria-label="Search"
          onClick={() => {
            window.dispatchEvent(new CustomEvent("proof:open-palette"));
          }}
          data-testid="desktop-topbar-search"
        >
          <IconSearch />
          <span>Search</span>
        </button>
      </div>
    </header>
  );
}
